import { t } from "../../i18n";
import { normalizeOrganizationPermissions } from "../../organization-permissions";

const organizationPermissions = [
  "providers:personal",
  "models:read",
  "models:write",
  "tools:use",
  "mcp:manage",
  "skills:write",
  "agent-workspaces:use",
  "users:read",
  "users:write",
  "usage:read",
  "billing:read",
  "billing:manage",
  "audit:read",
  "organization:manage",
  "organization:auth:manage",
  "policies:manage",
  "automations:use",
  "automations:manage",
  "integrations:use",
  "integrations:manage",
  "projects:manage",
  "chat:use",
] as const;

const permissionText: Record<
  (typeof organizationPermissions)[number],
  [string, string]
> = {
  "providers:personal": [
    "Connect personal providers",
    "Connect and use private model providers owned by this user.",
  ],
  "models:read": [
    "Use organization models",
    "View and use only models made available to this organization.",
  ],
  "models:write": [
    "Manage custom models",
    "Create and edit routing and fallback models for this organization or for yourself.",
  ],
  "tools:use": [
    "Use organization tools",
    "Run tools made available inside this organization.",
  ],
  "mcp:manage": [
    "Manage organization MCP connections",
    "Create and administer organization-scoped MCP servers without granting site-level permissions.",
  ],
  "skills:write": [
    "Manage organization skills",
    "Create and edit skills belonging to this organization; never site-wide skills.",
  ],
  "agent-workspaces:use": [
    "Use organization workspaces",
    "Use workspaces made available to this organization.",
  ],
  "users:read": ["View members", "View this organization's members."],
  "users:write": [
    "Manage members",
    "Invite, suspend, and set permissions for this organization's members.",
  ],
  "usage:read": ["View usage", "View usage generated by this organization."],
  "billing:read": [
    "View billing",
    "View this organization's billing information and invoices.",
  ],
  "billing:manage": [
    "Manage billing",
    "Manage this organization's billing identity and payment methods.",
  ],
  "audit:read": [
    "View audit log",
    "View security and administration events for this organization.",
  ],
  "organization:manage": [
    "Edit organization information",
    "Edit this organization's name, slug, locale, and timezone.",
  ],
  "organization:auth:manage": [
    "Manage authentication",
    "Configure native or OIDC login for this organization.",
  ],
  "policies:manage": [
    "Manage access policies",
    "Restrict organization resources for its users.",
  ],
  "automations:use": [
    "Use automations",
    "Create and run personal automations in this organization.",
  ],
  "automations:manage": [
    "Manage organization automations",
    "Create and administer automations owned by this organization.",
  ],
  "integrations:use": [
    "Use integrations",
    "Use personal and organization-connected integrations in automations.",
  ],
  "integrations:manage": [
    "Manage organization integrations",
    "Connect, edit, disable, and remove organization-owned integrations.",
  ],
  "projects:manage": [
    "Manage organization projects",
    "Create and edit projects shared with this organization.",
  ],
  "chat:use": ["Use chat", "Create conversations in this organization."],
};

export function OrganizationPermissionsEditor({
  value,
  onChange,
  personalProvidersEnabled = true,
}: {
  value: string[];
  onChange: (permissions: string[]) => void;
  personalProvidersEnabled?: boolean;
}) {
  const normalizedValue = normalizeOrganizationPermissions(value);
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {organizationPermissions.map((permission) => {
        const [label, description] = permissionText[permission];
        const disabled =
          permission === "providers:personal" && !personalProvidersEnabled;
        return (
          <label
            key={permission}
            className="flex items-start gap-3 rounded-xl border border-stone-200 p-3 dark:border-white/10 dark:bg-white/3"
          >
            <input
              className="mt-1"
              type="checkbox"
              checked={normalizedValue.includes(permission) && !disabled}
              disabled={disabled}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? normalizeOrganizationPermissions([
                        ...normalizedValue,
                        permission,
                      ])
                    : normalizedValue.filter((item) => item !== permission),
                )
              }
            />
            <span>
              <strong className="block text-sm">{t(label)}</strong>
              <span className="block text-xs text-stone-500 dark:text-stone-400">
                {disabled
                  ? t("Disabled by the organization's capabilities.")
                  : t(description)}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  );
}
